import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { DEFAULT_TZ, MT5_DEFAULT_TZ } from "./timezone";
import { useAuth } from "./auth";

export interface UserSettings {
  display_timezone: string;
  mt5_import_timezone: string;
  max_loss_pct: number | null;
  weekly_digest?: boolean;
  share_public?: boolean;
  share_token?: string | null;
  share_url?: string | null;
}

const SettingsCtx = createContext<{
  settings: UserSettings;
  isLoading: boolean;
  refresh: () => void;
}>({
  settings: {
    display_timezone: DEFAULT_TZ,
    mt5_import_timezone: MT5_DEFAULT_TZ,
    max_loss_pct: null,
  },
  isLoading: false,
  refresh: () => {},
});

export const settingsApi = {
  get: () => api<{ settings: UserSettings }>("/api/user-settings"),
  save: (payload: Partial<UserSettings>) =>
    api<{ settings: UserSettings }>("/api/user-settings", { method: "PATCH", body: payload }),
};

/**
 * SettingsProvider — reads the current user's settings from the API once auth
 * lands, then provides them to the whole app (format helpers, etc.). Written
 * as a wrapper of react-query so mutations elsewhere automatically refresh.
 */
export function SettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["user-settings"],
    queryFn: () => settingsApi.get(),
    enabled: !!user,
    staleTime: 60_000,
  });

  const qc = useQueryClient();
  const value = useMemo(
    () => ({
      settings: q.data?.settings ?? {
        display_timezone: DEFAULT_TZ,
        mt5_import_timezone: MT5_DEFAULT_TZ,
        max_loss_pct: null,
      },
      isLoading: q.isLoading,
      refresh: () => qc.invalidateQueries({ queryKey: ["user-settings"] }),
    }),
    [q.data, q.isLoading, qc],
  );

  return <SettingsCtx.Provider value={value}>{children}</SettingsCtx.Provider>;
}

export function useSettings() {
  return useContext(SettingsCtx);
}

/** Current display timezone. Safe to call outside React (falls back to default). */
export function getCurrentDisplayTz(): string {
  if (typeof window === "undefined") return DEFAULT_TZ;
  // We stash it on window from the provider so format helpers stay pure.
  const stashed = (window as unknown as { __journalTz?: string }).__journalTz;
  return stashed || DEFAULT_TZ;
}

export function setCurrentDisplayTz(tz: string): void {
  if (typeof window === "undefined") return;
  (window as unknown as { __journalTz?: string }).__journalTz = tz;
}

/** Hook that keeps window.__journalTz in sync so formatDate always sees it. */
export function useSyncDisplayTz() {
  const { settings } = useSettings();
  useMemo(() => setCurrentDisplayTz(settings.display_timezone), [settings.display_timezone]);
}

const useMutationTyped = useMutation;
export const useSaveSettings = () => {
  const qc = useQueryClient();
  return useMutationTyped({
    mutationFn: (payload: Partial<UserSettings>) => settingsApi.save(payload),
    onSuccess: (d) => {
      qc.setQueryData(["user-settings"], d);
      qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
    },
  });
};
